import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    PlusIcon,
    MagnifyingGlassIcon,
    Squares2X2Icon,
    ListBulletIcon,
    CheckIcon,
    ChevronUpDownIcon,
    ChatBubbleLeftIcon,
    UserCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
    FunnelIcon,
    CalendarDaysIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import { Button } from "src/components/base/button";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "src/components/base/dialog";
import { Input } from "src/components/base/input";
import { Textarea } from "src/components/base/textarea";
import { Select } from "src/components/base/select";
import { Field, Label } from "src/components/base/fieldset";
import type { ViewContext } from "../types";
import { SYSTEM_LIST } from "../types";
import { MOCK_TICKETS } from "../data/tickets";
import type { Ticket, TicketPriority, TicketStatus } from "../data/tickets";
import { StatCard, Card } from "../shared";

// ─── Constants ───────────────────────────────────────────────────────────────

const priorityColor = { critical: "red", high: "orange", medium: "yellow", low: "zinc" } as const;
const statusColor = {
    open: "red",
    "in-progress": "amber",
    waiting: "purple",
    resolved: "green",
    closed: "zinc",
} as const;

const KANBAN_COLUMNS: { key: TicketStatus; label: string; color: string; dotColor: string }[] = [
    { key: "open", label: "Open", color: "border-t-red-500", dotColor: "bg-red-500" },
    { key: "in-progress", label: "In Progress", color: "border-t-amber-500", dotColor: "bg-amber-500" },
    { key: "waiting", label: "Waiting", color: "border-t-purple-500", dotColor: "bg-purple-500" },
    { key: "resolved", label: "Resolved", color: "border-t-green-500", dotColor: "bg-green-500" },
    { key: "closed", label: "Closed", color: "border-t-zinc-500", dotColor: "bg-zinc-500" },
];

const PRIORITIES: TicketPriority[] = ["critical", "high", "medium", "low"];
const SYSTEMS = SYSTEM_LIST;
const ALL_ASSIGNEES = [...new Set(MOCK_TICKETS.map((t) => t.assignee))];

const MOCK_SLA_HOURS: Record<TicketPriority, number> = {
    critical: 2,
    high: 4,
    medium: 8,
    low: 24,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseMockTimestamp(ts: string): number {
    if (ts.includes("-")) return new Date(ts.replace(" ", "T")).getTime();
    const today = new Date();
    const [h, m] = ts.split(":").map(Number);
    today.setHours(h, m, 0, 0);
    return today.getTime();
}

function getSlaInfo(ticket: Ticket): { percentElapsed: number; remaining: string; overdue: boolean } {
    const createdMs = parseMockTimestamp(ticket.createdAt);
    const slaHours = MOCK_SLA_HOURS[ticket.priority];
    const slaDurationMs = slaHours * 60 * 60 * 1000;
    const now = new Date("2026-03-13T15:30:00").getTime();
    const elapsed = now - createdMs;
    const percentElapsed = Math.min((elapsed / slaDurationMs) * 100, 150);
    const remainingMs = slaDurationMs - elapsed;

    if (remainingMs <= 0) {
        const overdueMins = Math.abs(Math.round(remainingMs / 60000));
        const overHrs = Math.floor(overdueMins / 60);
        const overMin = overdueMins % 60;
        return { percentElapsed, remaining: overHrs > 0 ? `-${overHrs}h ${overMin}m` : `-${overMin}m`, overdue: true };
    }

    const remMins = Math.round(remainingMs / 60000);
    const hrs = Math.floor(remMins / 60);
    const mins = remMins % 60;
    return { percentElapsed, remaining: hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`, overdue: false };
}

function generateMockSparkline(seed: number, length: number = 7): number[] {
    const data: number[] = [];
    let v = (seed * 7 + 3) % 10;
    for (let i = 0; i < length; i++) {
        v = ((v * 13 + 7) % 10) + 1;
        data.push(v);
    }
    return data;
}

// ─── Animated Counter ────────────────────────────────────────────────────────

function AnimatedCounter({ target, duration = 800 }: { target: number; duration?: number }) {
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const start = performance.now();
        const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCurrent(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [target, duration]);

    return <>{current}</>;
}

// ─── Mini Sparkline (inline in table) ────────────────────────────────────────

function MiniSparkline({ data }: { data: number[] }) {
    const max = Math.max(...data);
    const h = 20;
    const w = 50;
    const step = w / (data.length - 1);
    const points = data.map((v, i) => `${i * step},${h - (v / max) * h}`).join(" ");

    return (
        <svg width={w} height={h} className="inline-block">
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-400" />
            {data.map((v, i) => (
                <circle key={i} cx={i * step} cy={h - (v / max) * h} r="1.5" className="fill-blue-500" />
            ))}
        </svg>
    );
}

// ─── Stat Sparkline Card ─────────────────────────────────────────────────────

function EnhancedStatCard({
    label,
    value,
    trend,
    trendDown,
    sparkData,
}: {
    label: string;
    value: number | string;
    trend?: string;
    trendDown?: boolean;
    sparkData: number[];
}) {
    const numValue = typeof value === "number" ? value : parseInt(value, 10);
    const isNum = !isNaN(numValue);

    return (
        <div className="group relative overflow-hidden rounded-2xl border border-zinc-200/40 bg-white/60 p-6 shadow-lg shadow-blue-500/[0.03] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/[0.08] dark:border-zinc-700/40 dark:bg-zinc-800/60">
            <div className="absolute inset-y-0 left-0 w-1 rounded-l-2xl bg-gradient-to-b from-blue-400 via-blue-500 to-purple-500" />
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/5 to-purple-500/5 blur-2xl transition-all duration-500 group-hover:from-blue-500/10 group-hover:to-purple-500/10" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-400 dark:text-zinc-500">
                {label}
            </p>
            <div className="mt-2 flex items-end justify-between gap-2">
                <p className="bg-gradient-to-r from-zinc-900 to-zinc-700 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent dark:from-white dark:to-zinc-300">
                    {isNum ? <AnimatedCounter target={numValue} /> : value}
                </p>
                <div className="h-8 w-16 opacity-50">
                    <MiniSparkline data={sparkData} />
                </div>
            </div>
            {trend && (
                <p
                    className={`mt-1 text-xs font-medium ${trendDown ? "text-emerald-600 dark:text-emerald-400" : "text-blue-600 dark:text-blue-400"}`}
                >
                    {trend}
                </p>
            )}
        </div>
    );
}

// ─── Priority Heatmap ────────────────────────────────────────────────────────

function PriorityHeatmap({ tickets }: { tickets: Ticket[] }) {
    const matrix = useMemo(() => {
        const m: Record<string, Record<string, number>> = {};
        for (const p of PRIORITIES) {
            m[p] = {};
            for (const s of SYSTEMS) {
                m[p][s] = tickets.filter((t) => t.priority === p && t.system === s).length;
            }
        }
        return m;
    }, [tickets]);

    const maxCount = useMemo(
        () => Math.max(1, ...Object.values(matrix).flatMap((row) => Object.values(row))),
        [matrix],
    );

    function intensityClass(count: number): string {
        if (count === 0) return "bg-zinc-100 dark:bg-zinc-800/50";
        const ratio = count / maxCount;
        if (ratio <= 0.25) return "bg-blue-200/70 dark:bg-blue-900/40";
        if (ratio <= 0.5) return "bg-blue-300/80 dark:bg-blue-800/50";
        if (ratio <= 0.75) return "bg-blue-500/70 dark:bg-blue-600/60";
        return "bg-blue-700/90 dark:bg-blue-500/80 text-white";
    }

    return (
        <Card className="mt-6">
            <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-white">Priority / System Heatmap</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr>
                            <th className="pb-2 pr-3 text-left font-medium text-zinc-500" />
                            {SYSTEMS.map((s) => (
                                <th
                                    key={s}
                                    className="px-2 pb-2 text-center font-medium uppercase tracking-wider text-zinc-500"
                                >
                                    {s}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {PRIORITIES.map((p) => (
                            <tr key={p}>
                                <td className="py-1.5 pr-3 text-right font-medium capitalize text-zinc-700 dark:text-zinc-300">
                                    {p}
                                </td>
                                {SYSTEMS.map((s) => (
                                    <td key={s} className="px-1 py-1">
                                        <div
                                            className={`flex h-9 w-full items-center justify-center rounded-lg font-mono font-bold transition-all duration-200 hover:scale-110 ${intensityClass(matrix[p][s])}`}
                                        >
                                            {matrix[p][s]}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

// ─── Kanban Card ─────────────────────────────────────────────────────────────

function KanbanCard({ ticket, onClick }: { ticket: Ticket; onClick: () => void }) {
    const initials = ticket.assignee
        .split(" ")
        .map((n) => n[0])
        .join("");

    return (
        <button
            onClick={onClick}
            className="group w-full rounded-xl border border-zinc-200/60 bg-white/80 p-3 text-left shadow-sm backdrop-blur-sm transition-all duration-200 hover:border-blue-300/60 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/60 dark:bg-zinc-800/80 dark:hover:border-blue-600/60"
        >
            {/* Drag handle dots */}
            <div className="mb-2 flex items-center justify-between">
                <div className="flex gap-0.5 opacity-30 group-hover:opacity-60">
                    <div className="grid grid-cols-2 gap-0.5">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="h-1 w-1 rounded-full bg-zinc-400" />
                        ))}
                    </div>
                </div>
                <span className="font-mono text-[10px] text-zinc-400">{ticket.id}</span>
            </div>

            <p className="truncate text-sm font-medium text-zinc-900 dark:text-white">{ticket.title}</p>

            <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge color={priorityColor[ticket.priority]}>{ticket.priority}</Badge>
                <Badge color="zinc">{ticket.system.toUpperCase()}</Badge>
            </div>

            <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-[10px] font-bold text-white shadow-sm">
                        {initials}
                    </div>
                    <span className="text-xs text-zinc-500">{ticket.assignee}</span>
                </div>
                {ticket.comments.length > 0 && (
                    <div className="flex items-center gap-0.5 text-zinc-400">
                        <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">{ticket.comments.length}</span>
                    </div>
                )}
            </div>
        </button>
    );
}

// ─── Kanban View ─────────────────────────────────────────────────────────────

function KanbanView({
    tickets,
    onNavigateDetail,
}: {
    tickets: Ticket[];
    onNavigateDetail: (ctx: ViewContext) => void;
}) {
    return (
        <div className="mt-4 grid grid-cols-5 gap-4 overflow-x-auto">
            {KANBAN_COLUMNS.map((col) => {
                const colTickets = tickets.filter((t) => t.status === col.key);
                return (
                    <div
                        key={col.key}
                        className={`rounded-xl border border-t-4 border-zinc-200/50 ${col.color} bg-zinc-50/50 p-3 dark:border-zinc-700/50 dark:bg-zinc-900/30`}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className={`h-2.5 w-2.5 rounded-full ${col.dotColor}`} />
                                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{col.label}</h4>
                            </div>
                            <span className="rounded-full bg-zinc-200/80 px-2 py-0.5 text-xs font-bold text-zinc-600 dark:bg-zinc-700/80 dark:text-zinc-400">
                                {colTickets.length}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {colTickets.map((tk) => (
                                <KanbanCard
                                    key={tk.id}
                                    ticket={tk}
                                    onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}
                                />
                            ))}
                            {colTickets.length === 0 && (
                                <div className="rounded-lg border-2 border-dashed border-zinc-200 py-8 text-center text-xs text-zinc-400 dark:border-zinc-700">
                                    No tickets
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── SLA Cell ────────────────────────────────────────────────────────────────

function SlaCell({ ticket }: { ticket: Ticket }) {
    if (ticket.status === "resolved" || ticket.status === "closed") {
        return <span className="text-xs text-zinc-400">--</span>;
    }

    const { percentElapsed, remaining, overdue } = getSlaInfo(ticket);

    let colorClass = "text-emerald-600 dark:text-emerald-400";
    let barColor = "bg-emerald-500";
    if (overdue) {
        colorClass = "text-red-600 dark:text-red-400 animate-pulse font-bold";
        barColor = "bg-red-500";
    } else if (percentElapsed > 80) {
        colorClass = "text-red-600 dark:text-red-400";
        barColor = "bg-red-500";
    } else if (percentElapsed > 50) {
        colorClass = "text-amber-600 dark:text-amber-400";
        barColor = "bg-amber-500";
    }

    return (
        <div className="flex flex-col gap-1">
            <span className={`text-xs font-semibold ${colorClass}`}>
                {overdue ? "OVERDUE " : ""}
                {remaining}
            </span>
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                <div
                    className={`h-full rounded-full ${barColor} transition-all duration-500`}
                    style={{ width: `${Math.min(percentElapsed, 100)}%` }}
                />
            </div>
        </div>
    );
}

// ─── Bulk Action Bar ─────────────────────────────────────────────────────────

function BulkActionBar({ count, onClear }: { count: number; onClear: () => void }) {
    return (
        <div className="animate-in slide-in-from-bottom-2 fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform">
            <div className="flex items-center gap-3 rounded-2xl border border-blue-200/60 bg-white/95 px-6 py-3 shadow-2xl shadow-blue-500/20 backdrop-blur-xl dark:border-blue-800/60 dark:bg-zinc-900/95">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                    {count}
                </span>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">selected</span>
                <div className="mx-2 h-6 w-px bg-zinc-200 dark:bg-zinc-700" />
                <Button color="blue" className="text-xs">
                    <UserCircleIcon className="h-3.5 w-3.5" />
                    Assign to...
                </Button>
                <Button color="amber" className="text-xs">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    Change Priority
                </Button>
                <Button color="red" className="text-xs">
                    <XMarkIcon className="h-3.5 w-3.5" />
                    Close Selected
                </Button>
                <div className="mx-2 h-6 w-px bg-zinc-200 dark:bg-zinc-700" />
                <button
                    onClick={onClear}
                    className="text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-700 dark:hover:text-zinc-300"
                >
                    Clear
                </button>
            </div>
        </div>
    );
}

// ─── Advanced Filters Panel ──────────────────────────────────────────────────

function AdvancedFiltersPanel({
    assigneeFilter,
    setAssigneeFilter,
    myTicketsOnly,
    setMyTicketsOnly,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
}: {
    assigneeFilter: string;
    setAssigneeFilter: (v: string) => void;
    myTicketsOnly: boolean;
    setMyTicketsOnly: (v: boolean) => void;
    dateFrom: string;
    setDateFrom: (v: string) => void;
    dateTo: string;
    setDateTo: (v: string) => void;
}) {
    return (
        <div className="mt-3 rounded-xl border border-zinc-200/50 bg-white/60 p-4 shadow-sm backdrop-blur-sm dark:border-zinc-700/50 dark:bg-zinc-800/60">
            <div className="flex flex-wrap items-end gap-4">
                <Field>
                    <Label className="text-xs">Assignee</Label>
                    <Select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
                        <option value="all">All Assignees</option>
                        {ALL_ASSIGNEES.map((a) => (
                            <option key={a} value={a}>
                                {a}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field>
                    <Label className="text-xs">Date From</Label>
                    <div className="flex items-center gap-1.5">
                        <CalendarDaysIcon className="h-4 w-4 text-zinc-400" />
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                </Field>
                <Field>
                    <Label className="text-xs">Date To</Label>
                    <div className="flex items-center gap-1.5">
                        <CalendarDaysIcon className="h-4 w-4 text-zinc-400" />
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                </Field>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-200/60 px-3 py-2 transition-all hover:border-blue-300 dark:border-zinc-700/60 dark:hover:border-blue-700">
                    <input
                        type="checkbox"
                        checked={myTicketsOnly}
                        onChange={(e) => setMyTicketsOnly(e.target.checked)}
                        className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">My Tickets</span>
                </label>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function TicketListView({ onNavigateDetail }: { onNavigateDetail: (ctx: ViewContext) => void }) {
    const { t } = useTranslation();
    const [showCreate, setShowCreate] = useState(false);
    const [filterSystem, setFilterSystem] = useState<string>("all");
    const [filterPriority, setFilterPriority] = useState<string>("all");
    const [filterStatus, setFilterStatus] = useState<string>("all");
    const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [assigneeFilter, setAssigneeFilter] = useState("all");
    const [myTicketsOnly, setMyTicketsOnly] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const tickets = useMemo(() => {
        return MOCK_TICKETS.filter((tk) => {
            if (filterSystem !== "all" && tk.system !== filterSystem) return false;
            if (filterPriority !== "all" && tk.priority !== filterPriority) return false;
            if (filterStatus !== "all" && tk.status !== filterStatus) return false;
            if (assigneeFilter !== "all" && tk.assignee !== assigneeFilter) return false;
            if (myTicketsOnly && tk.assignee !== "M. Schmidt") return false;
            if (dateFrom && tk.createdAt < dateFrom) return false;
            if (dateTo && tk.createdAt > dateTo + "z") return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                if (
                    !tk.title.toLowerCase().includes(q) &&
                    !tk.id.toLowerCase().includes(q) &&
                    !tk.description.toLowerCase().includes(q) &&
                    !tk.assignee.toLowerCase().includes(q) &&
                    !tk.system.toLowerCase().includes(q)
                )
                    return false;
            }
            return true;
        });
    }, [filterSystem, filterPriority, filterStatus, searchQuery, assigneeFilter, myTicketsOnly, dateFrom, dateTo]);

    const openCount = MOCK_TICKETS.filter((tk) => tk.status === "open" || tk.status === "in-progress").length;
    const criticalOpen = MOCK_TICKETS.filter(
        (tk) => tk.priority === "critical" && tk.status !== "resolved" && tk.status !== "closed",
    ).length;
    const waitingCount = MOCK_TICKETS.filter((tk) => tk.status === "waiting").length;

    const toggleTicket = useCallback((id: string) => {
        setSelectedTickets((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        setSelectedTickets((prev) => {
            if (prev.size === tickets.length) return new Set();
            return new Set(tickets.map((t) => t.id));
        });
    }, [tickets]);

    return (
        <>
            {/* ── Header ─────────────────────────────────────── */}
            <div className="flex items-center justify-between">
                <Heading>{t("tickets.title")}</Heading>
                <div className="flex items-center gap-3">
                    {/* View Toggle */}
                    <div className="flex overflow-hidden rounded-lg border border-zinc-200/60 shadow-sm dark:border-zinc-700/60">
                        <button
                            onClick={() => setViewMode("list")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                                viewMode === "list"
                                    ? "bg-blue-600 text-white shadow-inner"
                                    : "bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                            }`}
                        >
                            <ListBulletIcon className="h-4 w-4" />
                            List
                        </button>
                        <button
                            onClick={() => setViewMode("kanban")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all ${
                                viewMode === "kanban"
                                    ? "bg-blue-600 text-white shadow-inner"
                                    : "bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                            }`}
                        >
                            <Squares2X2Icon className="h-4 w-4" />
                            Kanban
                        </button>
                    </div>
                    <Button color="blue" onClick={() => setShowCreate(true)}>
                        <PlusIcon className="h-4 w-4" />
                        {t("tickets.create")}
                    </Button>
                </div>
            </div>

            <Button>Termin mit Techniker ausmachen</Button>

            {/* ── Enhanced Stats with sparklines ─────────────── */}
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <EnhancedStatCard
                    label={t("tickets.openTickets")}
                    value={openCount}
                    sparkData={[3, 5, 4, 7, 6, 8, openCount]}
                />
                <EnhancedStatCard
                    label={t("tickets.avgResolution")}
                    value="4.2h"
                    trend="-1.3h vs. last week"
                    trendDown
                    sparkData={[8, 7, 6, 5, 6, 5, 4]}
                />
                <EnhancedStatCard
                    label={t("tickets.criticalOpen")}
                    value={criticalOpen}
                    sparkData={[1, 2, 3, 2, 4, 3, criticalOpen]}
                />
                <EnhancedStatCard
                    label={t("tickets.waitingCustomer")}
                    value={waitingCount}
                    sparkData={[2, 3, 1, 2, 1, 2, waitingCount]}
                />
            </div>

            {/* ── Priority Heatmap ───────────────────────────── */}
            <PriorityHeatmap tickets={MOCK_TICKETS} />

            {/* ── Search + Filters ───────────────────────────── */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="relative min-w-[200px] max-w-sm flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search tickets..."
                        className="w-full rounded-lg border border-zinc-200/60 bg-white/60 py-2 pl-9 pr-3 text-sm shadow-sm backdrop-blur-sm transition-all placeholder:text-zinc-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:border-zinc-700/60 dark:bg-zinc-800/60 dark:text-white dark:placeholder:text-zinc-500 dark:focus:border-blue-500"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <Select value={filterSystem} onChange={(e) => setFilterSystem(e.target.value)}>
                    <option value="all">{t("tickets.allSystems")}</option>
                    <option value="soc">SOC</option>
                    <option value="ndr">NDR</option>
                    <option value="mail">Mail</option>
                    <option value="ztna">ZTNA</option>
                    <option value="phishing">Phishing</option>
                    <option value="pentests">Pentests</option>
                </Select>
                <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                    <option value="all">{t("tickets.allPriorities")}</option>
                    <option value="critical">{t("soc.critical")}</option>
                    <option value="high">{t("soc.high")}</option>
                    <option value="medium">{t("soc.medium")}</option>
                    <option value="low">{t("soc.low")}</option>
                </Select>
                <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">{t("tickets.allStatuses")}</option>
                    <option value="open">{t("tickets.statusOpen")}</option>
                    <option value="in-progress">{t("tickets.statusInProgress")}</option>
                    <option value="waiting">{t("tickets.statusWaiting")}</option>
                    <option value="resolved">{t("tickets.statusResolved")}</option>
                    <option value="closed">{t("tickets.statusClosed")}</option>
                </Select>
                <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                        showAdvancedFilters
                            ? "border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-950/30 dark:text-blue-400"
                            : "border-zinc-200/60 bg-white/60 text-zinc-600 hover:border-zinc-300 dark:border-zinc-700/60 dark:bg-zinc-800/60 dark:text-zinc-400"
                    }`}
                >
                    <FunnelIcon className="h-3.5 w-3.5" />
                    Advanced
                </button>
            </div>

            {/* ── Advanced Filters Panel ─────────────────────── */}
            {showAdvancedFilters && (
                <AdvancedFiltersPanel
                    assigneeFilter={assigneeFilter}
                    setAssigneeFilter={setAssigneeFilter}
                    myTicketsOnly={myTicketsOnly}
                    setMyTicketsOnly={setMyTicketsOnly}
                    dateFrom={dateFrom}
                    setDateFrom={setDateFrom}
                    dateTo={dateTo}
                    setDateTo={setDateTo}
                />
            )}

            {/* ── Results count ──────────────────────────────── */}
            {searchQuery && (
                <p className="mt-2 text-xs text-zinc-500">
                    Showing {tickets.length} of {MOCK_TICKETS.length} tickets
                    {searchQuery && <> matching &ldquo;{searchQuery}&rdquo;</>}
                </p>
            )}

            {/* ── Kanban or List View ────────────────────────── */}
            {viewMode === "kanban" ? (
                <KanbanView tickets={tickets} onNavigateDetail={onNavigateDetail} />
            ) : (
                <Table className="mt-4" dense>
                    <TableHead>
                        <TableRow>
                            <TableHeader>
                                <input
                                    type="checkbox"
                                    checked={selectedTickets.size === tickets.length && tickets.length > 0}
                                    onChange={toggleAll}
                                    className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                />
                            </TableHeader>
                            <TableHeader>{t("tickets.colId")}</TableHeader>
                            <TableHeader>{t("tickets.colTitle")}</TableHeader>
                            <TableHeader>{t("tickets.colPriority")}</TableHeader>
                            <TableHeader>{t("tickets.colStatus")}</TableHeader>
                            <TableHeader>SLA</TableHeader>
                            <TableHeader>{t("tickets.colSystem")}</TableHeader>
                            <TableHeader>{t("tickets.colAssignee")}</TableHeader>
                            <TableHeader>Activity</TableHeader>
                            <TableHeader>{t("tickets.colCreated")}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.map((tk, idx) => (
                            <TableRow
                                key={tk.id}
                                className={`cursor-pointer transition-colors ${selectedTickets.has(tk.id) ? "bg-blue-50/50 dark:bg-blue-950/20" : ""}`}
                            >
                                <TableCell>
                                    <input
                                        type="checkbox"
                                        checked={selectedTickets.has(tk.id)}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            toggleTicket(tk.id);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </TableCell>
                                <TableCell
                                    className="font-mono text-zinc-500"
                                    onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}
                                >
                                    {tk.id}
                                </TableCell>
                                <TableCell
                                    className="max-w-xs truncate font-medium"
                                    onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}
                                >
                                    {tk.title}
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <Badge color={priorityColor[tk.priority]}>{tk.priority}</Badge>
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <Badge color={statusColor[tk.status]}>{tk.status}</Badge>
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <SlaCell ticket={tk} />
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <Badge color="zinc">{tk.system.toUpperCase()}</Badge>
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <div className="flex items-center gap-1.5">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-[9px] font-bold text-white">
                                            {tk.assignee
                                                .split(" ")
                                                .map((n) => n[0])
                                                .join("")}
                                        </div>
                                        <span className="text-xs">{tk.assignee}</span>
                                    </div>
                                </TableCell>
                                <TableCell onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}>
                                    <MiniSparkline data={generateMockSparkline(idx + 1)} />
                                </TableCell>
                                <TableCell
                                    className="font-mono text-xs text-zinc-500"
                                    onClick={() => onNavigateDetail({ view: "ticket-detail", detailId: tk.id })}
                                >
                                    {tk.createdAt}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* ── Bulk Action Bar ────────────────────────────── */}
            {selectedTickets.size > 0 && (
                <BulkActionBar count={selectedTickets.size} onClear={() => setSelectedTickets(new Set())} />
            )}

            {/* ── Create Dialog ──────────────────────────────── */}
            <Dialog open={showCreate} onClose={() => setShowCreate(false)} size="lg">
                <DialogTitle>{t("tickets.createTitle")}</DialogTitle>
                <DialogBody>
                    <div className="space-y-4">
                        <Field>
                            <Label>{t("tickets.colTitle")}</Label>
                            <Input placeholder={t("tickets.titlePlaceholder")} />
                        </Field>
                        <Field>
                            <Label>{t("tickets.description")}</Label>
                            <Textarea rows={4} placeholder={t("tickets.descriptionPlaceholder")} />
                        </Field>
                        <div className="grid grid-cols-2 gap-4">
                            <Field>
                                <Label>{t("tickets.colSystem")}</Label>
                                <Select>
                                    <option value="soc">SOC</option>
                                    <option value="ndr">NDR</option>
                                    <option value="mail">Mail</option>
                                    <option value="ztna">ZTNA</option>
                                    <option value="phishing">Phishing</option>
                                    <option value="pentests">Pentests</option>
                                </Select>
                            </Field>
                            <Field>
                                <Label>{t("tickets.colPriority")}</Label>
                                <Select>
                                    <option value="medium">{t("soc.medium")}</option>
                                    <option value="low">{t("soc.low")}</option>
                                    <option value="high">{t("soc.high")}</option>
                                    <option value="critical">{t("soc.critical")}</option>
                                </Select>
                            </Field>
                        </div>
                    </div>
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setShowCreate(false)}>
                        {t("tickets.cancel")}
                    </Button>
                    <Button color="blue" onClick={() => setShowCreate(false)}>
                        {t("tickets.submit")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
